import { HSLColor } from "../../library-js/colors/HSLColor";
import { LinearGradient } from "../../library-js/colors/Gradient";
import { DateMath } from "../../library-js/date/DateMath";
import { ClassFlag } from "../../library-js/dom-tools/ClassFlag";
import { ElementType } from "../../library-js/dom-tools/ElementType";
import { Fullscreen } from "../../library-js/dom-actions/Fullscreen";
import { OnChange, OnClick, OnTimeUpdate } from "../../library-js/dom-tools/EventListeners";
import { DOMHitTest } from "../../library-js/dom-tools/DOMHitTest";
import { ElementAttribute } from "../../library-js/dom-tools/ElementAttribute";

export class VideoPlayer
{
  static readonly type = new ElementType( "video--player" );
  static readonly playButton = new ElementType( "play--button" );
  static readonly fullscreenButton = new ElementType( "fullscreen--button" );
  static readonly audioButton = new ElementType( "audio--button" );
  static readonly loopButton = new ElementType( "loop--button" );
  static readonly progressInfo = new ElementType( "progress--info" );
  static readonly progressIndicator = new ElementType( "progress--indicator" );

  static readonly alwaysMuted = new ClassFlag( "always-muted" );
  static readonly noLoop = new ClassFlag( "no-loop" );
  static readonly looping = new ElementAttribute( "looping" );
  static readonly halfSpeed = new ClassFlag( "half-speed" );
  static readonly quarterSpeed = new ClassFlag( "quarter-speed" );
  static readonly playsInline = new ClassFlag( "plays-inline" );
  static readonly autoPlay = new ClassFlag( "auto-play" );

  static applyOnDocument()
  {
    VideoPlayer.type.forAllInDoc( e => VideoPlayer.applyOnElement( e ) );
    
  }

  static pauseVideo( e:Element )
  {
    ClassFlag.playing.removeFrom( e );
    let video = ElementType.video.query( e );
    video.pause();
  }

  static applyOnElement( e:Element ) 
  {
    let playButton = VideoPlayer.playButton.query( e );
    let loopButton = VideoPlayer.loopButton.query( e ); 
    let selectSpeed = ElementType.select.query( e );

    let video = ElementType.video.query( e );
    let progressInfo = VideoPlayer.progressInfo.query( e ) as HTMLElement;
    let progressIndicator = VideoPlayer.progressIndicator.query( e ) as HTMLElement;
    let fs = VideoPlayer.fullscreenButton.query( e ); 
    let au = VideoPlayer.audioButton.query( e ) as HTMLElement; 
    let alwaysMuted = VideoPlayer.alwaysMuted.in( e );

    if ( VideoPlayer.noLoop.in( e ) )
    {
      video.loop = false;
    }

    

    VideoPlayer.looping.to( e, video.loop + "" );

    if ( VideoPlayer.halfSpeed.in( e ) )
    {
      video.playbackRate = 0.5;
    }

    if ( VideoPlayer.quarterSpeed.in( e ) )
    {
      video.playbackRate = 0.25;
    }

    if ( VideoPlayer.playsInline.in( e ) )
    {
      video.playsInline = true;
      video.setAttribute( "playsinline", "" );
      video.setAttribute( "webkit-playsinline", "" );
    }

    if ( selectSpeed )
    {
      let mappedSpeed = new Map();
      let speedLabels = 
      [
        "1/8x","1/4x", "1/2x", "1x", "2x", "4x"
      ];

      let speedValues = 
      [
        0.125, 0.25, 0.5, 1, 2, 4
      ];

      for ( let i = 0; i < speedLabels.length; i++ )
      {
        mappedSpeed.set( speedLabels[ i ], speedValues[ i ] );
      }

      
      let selected = ElementType.option.queryAll( selectSpeed ).findIndex( o => ElementAttribute.selected.in( o ) );

      for ( let i = 0; i < speedLabels.length; i++ )
      {
        let k = speedLabels[ i ];
        let v = mappedSpeed[ k ];

        if ( Math.abs( v - video.playbackRate ) < 0.01 )
        {
          selected = i;
        }
      }


      selectSpeed.selectedIndex = selected;
      
      OnChange.add( selectSpeed, 
        ()=>
        {
          let value = selectSpeed.value;
          

          if ( mappedSpeed.has( value ) )
          {
            video.playbackRate = mappedSpeed.get( value );
          }
        } 
      );

    }

    ( ( video ) as any )._wasPlayed = false;

    if ( loopButton )
    {
      OnClick.add( loopButton as HTMLElement, 
        ()=>
        {
          let isLooping = ! video.loop;
          video.loop = isLooping;
          VideoPlayer.looping.to( e, isLooping + "" );
        }
      );
    }
   

    OnClick.add( playButton as HTMLElement, 
      ()=>
      {
        let isPaused = video.paused;

        console.log( "Cl" );
        // alert( "Is Paused:" + isPaused );

        if ( isPaused )
        {
          if ( ! ( ( video ) as any )._wasPlayed )
          {
            if ( ! alwaysMuted )
            {
              video.muted = false;
              video.volume = 1;
              ElementAttribute.muted.to( e, false );
            }
          }

          ClassFlag.playing.set( e );
          ( ( video ) as any )._wasPlayed = true;
          video.play();          
          
        }
        else
        { 
          ClassFlag.playing.removeFrom( e );
          video.pause();
          
        } 
        
      }
    )

    if ( VideoPlayer.autoPlay.in( e ) )
    {
      video.autoplay = true;
      ClassFlag.playing.set( e );
    }

    OnClick.add( au, 
      ( ev )=>
      {
        if ( alwaysMuted )
        {
          return;
        }

        let isMuted = ! ElementAttribute.muted.in( video );

        if ( isMuted )
        {
          ElementAttribute.muted.to( video );
        }
        else
        {
          ElementAttribute.muted.removeFrom( video );
        }

        video.muted = isMuted;
        video.volume = isMuted ? 0 : 1;

        ElementAttribute.muted.to( e, isMuted );

        ev.preventDefault();
        ev.stopImmediatePropagation();
        ev.stopPropagation();
      } 
    )

    let updateVideoTimeCode = ( text:string = null )=>
      {
        let textValue = text != null ? text : ( DateMath.formatToMinutesAndSeconds( video.currentTime ) + " / " + 
                       DateMath.formatToMinutesAndSeconds(  video.duration ) );
  
        progressInfo.innerHTML = `${textValue}`;
        let percent = text != null ? 0 : (video.currentTime / video.duration) * 100;
        let leftColor = new HSLColor( 0, 0, 100, 0.3 );
        let rightColor = new HSLColor( 0, 0, 100, 0.1 );
  
        let linearBG = LinearGradient.createHorizontal( leftColor, rightColor, percent, 1 );
  
        progressIndicator.style.background = linearBG + ", hsl(0,0%,0%,0.3)";
      };
  
    OnTimeUpdate.add( video, () => { updateVideoTimeCode(); } );

    OnClick.add( progressIndicator, 
      ( e )=>
      {
        let relative = DOMHitTest.getNormalizedPointerPosition( e, progressIndicator );
        let position = Math.round( video.duration * relative.x );
        video.currentTime = position;

        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    );

    OnClick.add( fs as HTMLElement, 
      ( ev )=>
      { 

        Fullscreen.toggleFullscreen( e as HTMLElement, "auto" );         



        if ( video.paused )
        {
          video.play();
          ClassFlag.playing.set( e );
        }

        ev.preventDefault();
        ev.stopImmediatePropagation();
        ev.stopPropagation();

      } 
    );
  }
}